import { BasePlugin, BaseStats, ErrorInfo, RequestInfo, PerformanceEvent, mergePluginOption } from '@fpkg/stats-core';
import { formatDate } from '@fpkg/stats-utils';
import { nanoid } from 'nanoid';
import WebStats from '../WebStats';
import { SFLogsOpt } from './SFLogsPlugin';
import { getCookie, getBrowserInfo, isNeedReport } from '../utils';

const { browserType = '', browserVersion = '' } = getBrowserInfo();

export enum SFAPMMonitorType {
  performance = 'performance',
  http = 'http',
  jsError = 'jsError',
  customError = 'customError',
}

export type JSErrorStackFrameType = {
  filename: string;
  function: string;
  in_app: boolean;
  lineno?: number;
  colno?: number;
};

export type SFAPMCustomErrorType = {
  level: 'error' | 'warn' | 'info' | 'log';
  event: string;
  msg: string;
};

// 大前端APM上报数据类型 https://confluence.sf-express.com/pages/viewpage.action?pageId=346426527
export type SFAPMReportDataType = {
  monitorType: SFAPMMonitorType;
  appCode: string;
  userCode: string;
  reportId: string;
  page: string;
  documentTilte: string;
  sdk: string;
  sessionId: string;
  browserType: string;
  browserVersion: string;
  deviceId: string;
  browserScreen: {
    width: number;
    height: number;
  };
  createTime: string;
  microApp?: string;
  performance?: any;
  jsErrorReport?: {
    level: string;
    errorInfo: {
      type: string;
      value: string;
      stacktrace?: {
        frames: Array<JSErrorStackFrameType>;
      };
    };
  };
  httpReport?: {
    errorDesc: string; // 错误描述
    level: string; // 错误等级
    param: string; // 请求入参
    requestMethod: string; // 请求方式（POST/GET...）
    requestTime: string; // 请求发起的时间
    statusCode: string | number; // 请求响应码
    url: string; // 请求地址
    path: string;
    sw8: string;
    startTimestamp: number;
    endTimestamp: number;
    response: string;
  };
  customError?: SFAPMCustomErrorType;
};

export interface BaseSFAPMConfig {
  // 上报的接口
  dsn: string;
  // 应用ID，需要在管理后台配置
  appCode: string;
  // 用于上报状态码为200的请求, 如返回true, 就会上报该条状态码为200的请求。非必填
  canCatchHttp200?: (params: { url: string; status: string; response: JSON; params?: any }) => boolean;
}

export default class SFAPMPlugin implements BasePlugin {
  name = 'sfapm';

  stats?: WebStats;

  option: BaseSFAPMConfig = {
    // 上报的接口
    dsn: 'https://app-apm-apis.sf-express.com/apmReport/report/frontend',
    // 应用ID，需要在管理后台配置
    appCode: '',
    // 用于上报状态码为200的请求, 如返回true, 就会上报该条状态码为200的请求。非必填
    canCatchHttp200: isNeedReport,
  };

  ReportRecord: Record<string, number> = {};

  constructor(option: SFLogsOpt | null | boolean) {
    mergePluginOption(this.option, option);
  }

  /**
   * 同一个请求的相同异常1分钟（告警最小时间单元）内只上报一次
   *  对于请求url、method、参数相同就认为是同一个请求；
   *  发生的异常类型、返回状态码和message相同，认为是同一个异常；
   */
  checkWhetherReportedTheSameHttpError(eventInfo: RequestInfo): boolean {
    const now = Date.now();
    // eslint-disable-next-line max-len
    const reportKey = `${eventInfo.http_method}${eventInfo.http_url}${eventInfo.http_params}${eventInfo.http_status}${eventInfo.type}${eventInfo.error_message}`;
    const isSame = !!this.ReportRecord[reportKey] && now - this.ReportRecord[reportKey] < 60000;
    if (!isSame) {
      this.ReportRecord[reportKey] = now;
    }
    return isSame;
  }

  /**
   * 上报到大前端APM
   */
  sendSFAPM(reportData: SFAPMReportDataType) {
    this.stats?.sender.request({
      url: this.option.dsn || 'https://app-apm-apis.sf-express.com/apmReport/report/frontend',
      method: 'post',
      data: reportData,
    });
  }

  generateCommonParams(monitorType: SFAPMMonitorType, config: BaseSFAPMConfig): SFAPMReportDataType {
    return {
      monitorType,
      appCode: config.appCode || '',
      userCode: this.stats?.userId || '',
      reportId: nanoid(),
      page: window.location.href,
      documentTilte: document.title,
      sdk: this.stats?.config.app_version || '',
      sessionId: getCookie('SESSION') || '',
      browserType,
      browserVersion,
      deviceId: this.stats?.nanoid || '',
      browserScreen: {
        width: window.screen.width,
        height: window.screen.height,
      },
      createTime: formatDate(Date.now()),
    };
  }

  reportPerformanceToSFAPM(eventInfo: PerformanceEvent, config: BaseSFAPMConfig) {
    const commonParams = this.generateCommonParams(SFAPMMonitorType.performance, config);

    if (eventInfo.micro_app) {
      commonParams.microApp = eventInfo.micro_app as string;
    }
    // onLoad 事件采集的性能数据
    if (eventInfo.load_time || eventInfo.load_time === 0 || eventInfo.fp || eventInfo.fp === 0) {
      commonParams.performance = {
        dns: eventInfo.dns_time || 0, // DNS 解析时间
        domReady: eventInfo.dom_time || 0, // dom解析时长

        fcp: eventInfo.fcp_time || 0, // 首屏时长
        fp: eventInfo.fp_time || 0, // 白屏时长
        ttfb: eventInfo.ttfb_time || 0, // 首字节时长
        tcp: eventInfo.tcp_time || 0, // tcp时长

        loadEvent: eventInfo.load_time || 0, // 资源加载时长
        loadPage: eventInfo.load_time || 0, // 页面加载时长
        loadSdk: 0, // sdk加载时长
        response: eventInfo.waiting_ttfb_time || 0, // 请求响应时长
        transmission: eventInfo.req_time || 0, // 内容传输时长
      };
      this.sendSFAPM(commonParams);
    } else if (eventInfo.lcp_time) {
      // lcp 事件采集的性能数据
      commonParams.performance = {
        type: 'lcp',
        lcp: eventInfo.lcp_time,
      };
      this.sendSFAPM(commonParams);
    }
  }

  reportJsErrorToSFAPM(eventInfo: ErrorInfo, config: BaseSFAPMConfig) {
    const commonParams = this.generateCommonParams(SFAPMMonitorType.jsError, config);
    const stackFrames = eventInfo.stack_frames ? eventInfo.stack_frames : [];
    commonParams.jsErrorReport = {
      errorInfo: {
        type: eventInfo.type,
        value: eventInfo.message,
        stacktrace: {
          frames: stackFrames as any,
        },
      },
      level: 'error',
    };
    if (eventInfo.micro_app) {
      commonParams.microApp = eventInfo.micro_app as string;
    }
    this.sendSFAPM(commonParams);
  }

  reportHttpToSFAPM(eventInfo: RequestInfo, config: BaseSFAPMConfig) {
    let needReport = false;
    let response: any = {};
    const params = eventInfo.http_method === 'post' ? eventInfo.http_params : eventInfo.http_query; // 请求入参

    try {
      response =
        typeof eventInfo.response_body === 'string'
          ? JSON.parse(eventInfo.response_body || '{}')
          : eventInfo.response_body;
    } catch (e) {
      response = {};
      console.warn('JSON.parse报错了', response, e);
    }

    try {
      const checkData = {
        url: eventInfo.http_url,
        status: eventInfo.http_status,
        response,
        params,
        microApp: eventInfo.micro_app,
      };
      const isDSN = checkData.url === this.option.dsn;
      const defaultCheckResult = isNeedReport(checkData);
      if (isDSN) {
        needReport = false;
      } else if (config.canCatchHttp200 && defaultCheckResult) {
        // 默认的规则不通过就不再计算业务的规则
        needReport = config.canCatchHttp200(checkData);
      } else {
        needReport = defaultCheckResult;
      }
    } catch (e) {
      console.error('业务配置的 canCatchHttp200 方法报错了', e);
    }

    if (needReport && eventInfo.http_url !== config.dsn && !this.checkWhetherReportedTheSameHttpError(eventInfo)) {
      const commonParams = this.generateCommonParams(SFAPMMonitorType.http, config);
      commonParams.httpReport = {
        errorDesc: eventInfo.error_message || eventInfo.type,
        level: 'error',
        param: params, // 请求入参
        requestMethod: eventInfo.http_method,
        requestTime: formatDate(Date.now()), // 请求发起的时间
        statusCode: eventInfo.http_status,
        url: eventInfo.http_url,
        path: eventInfo.http_path || '',
        sw8: eventInfo.traceid,
        startTimestamp: eventInfo.start_time,
        endTimestamp: eventInfo.end_time,
        response: eventInfo.response_body || '',
      };
      if (eventInfo.micro_app) {
        commonParams.microApp = eventInfo.micro_app as string;
      }
      this.sendSFAPM(commonParams);
    }
  }

  reportCustomErrorToSFAPM(eventInfo: SFAPMCustomErrorType) {
    if (eventInfo.event && eventInfo.level) {
      const sfapmConfig = this.option;
      if (sfapmConfig && sfapmConfig.appCode) {
        const commonParams = this.generateCommonParams(SFAPMMonitorType.customError, sfapmConfig);
        commonParams.customError = eventInfo;
        this.sendSFAPM(commonParams);
      } else {
        console.warn('未配置APM组件，不能上报APM');
      }
    } else {
      console.error('上报APM自定事件缺少必要参数，请检查您上报的内容');
    }
  }

  reportCatchError(error: any) {
    // 捕获异常，确保不影响其他上报流程
    console.error('上报APM报错', error);
    // 上报神策
    this.stats?.customError({
      event: 'StatsError',
      event_code: 'ReportSFAMPError',
      event_name: '上报APM报错',
      plugin_name: this.name,
      error,
    });
  }

  setup(stats: BaseStats): void {
    this.stats = stats as WebStats;
    const sfapmConfig = this.option;

    // 有配置 apm 的 appCode 就说明要上报到amp
    if (sfapmConfig && sfapmConfig.appCode) {
      this.stats.$bus.$on('ON_JS_ERROR', (info: ErrorInfo, stackFrames: any = []) => {
        try {
          this.reportJsErrorToSFAPM(
            {
              ...info,
              stack_frames: stackFrames,
            },
            sfapmConfig
          );
        } catch (e) {
          this.reportCatchError(e);
        }
      });
      this.stats.$bus.$on('ON_AJAX_EVENT', (info: RequestInfo) => {
        try {
          this.reportHttpToSFAPM(info, sfapmConfig);
        } catch (e) {
          this.reportCatchError(e);
        }
      });
      this.stats.$bus.$on('ON_PERFORMANCE_EVENT', (info: PerformanceEvent) => {
        try {
          this.reportPerformanceToSFAPM(info, sfapmConfig);
        } catch (e) {
          this.reportCatchError(e);
        }
      });
    }
  }
}
